import { Box, Menu, MenuItem, TextField, InputAdornment, IconButton, Divider } from '@mui/material';
import { Button, colors, Typography } from '@sweep-io/sweep-design';
import { ChevronRight, Close, Check, ArrowLeft, Info } from '@sweep-io/sweep-design/dist/icons';
import { useCallback, useState, useMemo, useEffect } from 'react';
import { TeamsRecipientInfo, TeamsRecipientType } from './teams/types';
import { useIntegrations } from '../../hooks/useIntegrations';
import teamsPng from './images/teamsIcon.png';
import debounce from 'lodash/debounce';
import { DEAL_ROOM_OPTION } from './teams/constants';

interface TeamsRecipientsNestedSelectorProps {
  onChange: (recipient: TeamsRecipientInfo) => void;
  readonly?: boolean;
  customButtonText?: string;
  selectedRecipients?: TeamsRecipientInfo[];
  showDealRoomOption?: boolean;
  usersOnly?: boolean;
}

const TEAMS_ONLY_THRESHOLD = 499;

export const TeamsRecipientsNestedSelector = ({
  onChange,
  readonly,
  customButtonText = 'Add Recipient',
  selectedRecipients = [],
  showDealRoomOption = false,
  usersOnly = false,
}: TeamsRecipientsNestedSelectorProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedTeam, setSelectedTeam] = useState<{ id: string; label: string } | null>(null);
  const [menuItems, setMenuItems] = useState<TeamsRecipientInfo[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [userCount, setUserCount] = useState<number | null>(null);
  const [isSearching, setIsSearching] = useState(false);

  const { getTeamsRecipients, getTeamsChannels, getTeamsUsersCount, searchTeamsRecipients } =
    useIntegrations();

  const debouncedSearch = useMemo(
    () =>
      debounce(async (query: string) => {
        const isTeamsOnly = userCount && userCount > TEAMS_ONLY_THRESHOLD;
        if (!query) {
          const recipients = await getTeamsRecipients(
            usersOnly ? TeamsRecipientType.USER : isTeamsOnly ? TeamsRecipientType.TEAM : undefined,
          );
          setMenuItems([...recipients, ...(showDealRoomOption ? [DEAL_ROOM_OPTION] : [])]);
          return;
        }
        setIsSearching(true);
        try {
          const results = await searchTeamsRecipients(query);
          setMenuItems([...results, ...(showDealRoomOption ? [DEAL_ROOM_OPTION] : [])]);
        } finally {
          setIsSearching(false);
        }
      }, 300),
    [getTeamsRecipients, searchTeamsRecipients, userCount, showDealRoomOption, usersOnly],
  );

  const fetchInitialMenuItems = useCallback(async () => {
    let count = userCount;
    if (userCount === null) {
      count = await getTeamsUsersCount();
      setUserCount(count);
    }
    const recipients = await getTeamsRecipients(
      usersOnly
        ? TeamsRecipientType.USER
        : count && count > TEAMS_ONLY_THRESHOLD
          ? TeamsRecipientType.TEAM
          : undefined,
    );
    setMenuItems([...recipients, ...(showDealRoomOption ? [DEAL_ROOM_OPTION] : [])]);
  }, [userCount, getTeamsUsersCount, getTeamsRecipients, showDealRoomOption, usersOnly]);

  const handleClick = useCallback(
    async (event: React.MouseEvent<HTMLElement>) => {
      if (readonly) return;
      setAnchorEl(event.currentTarget);
      setSelectedTeam(null);
      setIsLoading(true);
      try {
        await fetchInitialMenuItems();
      } finally {
        setIsLoading(false);
      }
    },
    [readonly, fetchInitialMenuItems],
  );

  useEffect(() => {
    if (!anchorEl) return;
    if (selectedTeam) return;
    if (searchQuery) {
      if (userCount && userCount > TEAMS_ONLY_THRESHOLD && !usersOnly) {
        debouncedSearch(searchQuery);
      }
      // else: do nothing, local filtering will handle it
    } else {
      fetchInitialMenuItems();
    }
    return () => {
      debouncedSearch.cancel();
    };
  }, [
    searchQuery,
    debouncedSearch,
    selectedTeam,
    fetchInitialMenuItems,
    anchorEl,
    userCount,
    usersOnly,
  ]);

  const filteredMenuItems = useMemo(() => {
    if (selectedTeam) {
      if (!searchQuery) return menuItems;
      return menuItems.filter((item) =>
        item.label.toLowerCase().includes(searchQuery.toLowerCase()),
      );
    }
    if (!searchQuery) return menuItems;
    if (userCount && userCount > TEAMS_ONLY_THRESHOLD && !usersOnly) {
      // Server results already in menuItems
      return menuItems;
    }
    // Local filter for users/teams (including when usersOnly is true)
    return menuItems.filter((item) => item.label.toLowerCase().includes(searchQuery.toLowerCase()));
  }, [menuItems, searchQuery, userCount, selectedTeam, usersOnly]);

  const isItemSelected = useCallback(
    (recipient: TeamsRecipientInfo) => {
      return selectedRecipients.some(
        (selected: TeamsRecipientInfo) => selected.id === recipient.id,
      );
    },
    [selectedRecipients],
  );

  const handleClose = useCallback(() => {
    setAnchorEl(null);
    setSelectedTeam(null);
    setSearchQuery('');
  }, []);

  const handleMenuItemClick = useCallback(
    async (recipient: TeamsRecipientInfo) => {
      if (recipient.type === 'team') {
        setIsLoading(true);
        try {
          const channels = await getTeamsChannels(recipient.id as string);
          setSelectedTeam({ id: recipient.id as string, label: recipient.label });
          setMenuItems(channels);
          setSearchQuery('');
        } finally {
          setIsLoading(false);
        }
      } else {
        onChange(recipient);
        handleClose();
      }
    },
    [getTeamsChannels, onChange, handleClose],
  );

  const handleBack = useCallback(async () => {
    setIsLoading(true);
    setSearchQuery('');
    try {
      const isTeamsOnly = userCount && userCount > TEAMS_ONLY_THRESHOLD;
      const recipients = await getTeamsRecipients(
        usersOnly ? TeamsRecipientType.USER : isTeamsOnly ? TeamsRecipientType.TEAM : undefined,
      );
      setSelectedTeam(null);
      setMenuItems(recipients);
    } finally {
      setIsLoading(false);
    }
  }, [getTeamsRecipients, userCount, usersOnly]);

  const handleClearSearch = useCallback(() => {
    setSearchQuery('');
  }, []);

  return (
    <Box>
      <Button
        size="medium"
        onClick={handleClick}
        startIconName="Plus"
        variant="flat-blue"
        disabled={readonly || isLoading}
      >
        {customButtonText}
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        sx={{ p: 1 }}
        PaperProps={{
          sx: {
            maxHeight: 300,
            minWidth: '310px',
            mt: 1,
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <Box
          sx={{
            p: 1,
            backgroundColor: colors.white,
            position: 'sticky',
            top: 0,
            zIndex: 1,
          }}
        >
          <TextField
            fullWidth
            size="small"
            placeholder="Search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              endAdornment: searchQuery ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={handleClearSearch}
                    sx={{ color: colors.grey[500] }}
                  >
                    <Close />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                height: '32px',
                fontSize: '14px',
              },
            }}
          />
        </Box>

        <Box sx={{ overflow: 'auto' }}>
          {selectedTeam && (
            <>
              <MenuItem onClick={handleBack}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ArrowLeft />
                  <Typography variant="body-bold">Back to {selectedTeam.label}</Typography>
                </Box>
              </MenuItem>
              <Divider />
            </>
          )}
          {filteredMenuItems.length > 0 ? (
            filteredMenuItems.map((recipient) => {
              const isSelected = isItemSelected(recipient);
              return (
                <MenuItem
                  key={recipient.id as string}
                  onClick={() => (isSelected ? undefined : handleMenuItemClick(recipient))}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    justifyContent: 'space-between',
                    backgroundColor: isSelected ? colors.grey[100] : 'transparent',
                    '&:hover': {
                      backgroundColor: isSelected ? colors.grey[200] : colors.grey[100],
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <img src={teamsPng} style={{ width: '16px', height: '16px' }} />
                    <Typography variant={'body'}>{recipient.label}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {isSelected && <Check color={colors.blue[500]} />}
                    {recipient.type === 'team' && <ChevronRight color={colors.grey[500]} />}
                  </Box>
                </MenuItem>
              );
            })
          ) : !isLoading && !isSearching ? (
            <MenuItem disabled>
              <Typography variant={'body'} color={colors.grey[600]}>
                No results found
              </Typography>
            </MenuItem>
          ) : null}
          {(isLoading || isSearching) && (
            <MenuItem disabled>
              <Typography variant={'body'}>Loading...</Typography>
            </MenuItem>
          )}
          {!(isLoading || isSearching) &&
            userCount &&
            userCount > TEAMS_ONLY_THRESHOLD &&
            !searchQuery &&
            !usersOnly && (
              <Box
                sx={{
                  p: 2,
                  display: 'flex',
                  alignItems: 'center',
                  color: colors.grey[500],
                  gap: 1,
                }}
              >
                <Info variant="tiny" color={colors.grey[700]} />
                <Typography variant="caption" color={colors.grey[700]}>
                  List too long - search to find users.
                </Typography>
              </Box>
            )}
        </Box>
      </Menu>
    </Box>
  );
};
